<template>
  <div>
    <div class='flex flex-col justify-center items-center uppercase mt-6'>
      <h1 class='font-bold text-sm'>Voting Sessions <span class='font-normal'>({{ sessionCountFortodaysDate }})</span></h1>
      <h2 class='text-xs text-gray-500'>{{ subtitleString }}</h2>
    </div>
    <div
      v-if='hasVotingsForToday'
      class='relative pt-8 pb-16 px-2 lg:px-0' 
      style='min-height:calc(100vh - 28rem);'>
      <div v-for='session in todaysDateSessionVotings'
        :key='`session-voting-${session.id}`'
        :class="['mb-8 p-4 rounded-lg', isLiveSession(session) ? 'bg-pink-50' : 'opacity-30']"
        @click='goToQuestion(session)'>
        <h1 class='text-lg font-medium text-gray-900'>
          {{session.title}}
        </h1>
        <div class='flex flex-row space-x-2'>
          <span
            class='text-sm'>
            {{formattedTimeString(session.scheduledStartTime)}} - {{formattedTimeString(session.scheduledEndTime)}}
          </span>
          <div v-if='isPastSession(session)'
            class='flex items-center text-xs text-gray-500 uppercase border rounded-md px-1'>
            Past
          </div>
          <div v-if='isLiveSession(session)'
            class='flex items-center text-pink-500 uppercase text-xs'>
            <span class='inline-block w-2 h-2 bg-pink-500 rounded-full mr-1'></span>
            Live
          </div>
          <div v-if='isNotStartedSession(session)'
            class='flex items-center text-xs text-gray-900 uppercase border rounded-md border-gray-900 px-1 font-bold'
            style='opacity: 1;'>
            <span class=''>{{ startingTime(session) }}</span>
          </div>
        </div>
      </div>
    </div>
    <div v-else
      class='flex flex-col justify-center items-center py-8'>
      <h1 class='text-sm text-gray-500'>No voting sessions scheduled for today</h1>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import DateTimeStringHelpers from '@/utils/date-time-string-helpers'
import dayjs from 'dayjs'
import utc      from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone' 
dayjs.extend(utc)
dayjs.extend(timezone)

export default {
  name: 'SessionVotings',
  data () {
    return {
      updateVotingQuestionsTimer: null
    }
  },
  computed: {
    ...mapState('sessionVoting', [
      'sessionVotingQuestions',
    ]),
    ...mapGetters('sessionVoting', [
      'hasVotingQuestions',
    ]),
    ...mapGetters('users', [
      'isAdmin',
    ]),
    sessionDates () {
      return this.sessionVotingQuestions.map(sessionVotings => {
        return dayjs(sessionVotings.date).format('YYYY-MM-DD')
      })
    },
    todaysDateSessionVotings () {
      let todaysDate = dayjs(new Date()).tz("Asia/Seoul").format('YYYY-MM-DD')
      let index = this.sessionDates.findIndex(date => date === todaysDate)

      if (this.isAdmin && this.hasVotingQuestions) {
        return this.sessionVotingQuestions
          .flatMap(entry => entry.listOfSessions)
          .sort((a, b) => dayjs(a.scheduledStartTime).isBefore(dayjs(b.scheduledStartTime)) ? -1 : 1)
      }

      if  (index > -1 && this.hasVotingQuestions) {
        return [...this.sessionVotingQuestions[index].listOfSessions].sort((a, b) => dayjs(a.scheduledStartTime).isBefore(dayjs(b.scheduledStartTime)) ? -1 : 1)
      } else {
        return []
      } 
    },
    todaysDate() {
      let todaysDate = dayjs(new Date()).tz("Asia/Seoul")
      return DateTimeStringHelpers.toMonthDay(todaysDate)
    },
    sessionCountFortodaysDate() {
      return this.todaysDateSessionVotings.length
    },
    hasVotingsForToday() {
      return this.todaysDateSessionVotings.length > 0
    },
    subtitleString() {
      if (this.isAdmin) {
        return 'Admin View (All Sessions)'
      } else {
        return `For ${this.todaysDate}`
      }
    },
  },
  methods: {
    ...mapActions('sessionVoting', [
      'getSessionVotingQuestions',
    ]),
    goToQuestion (session) {
      if (!this.isLiveSession(session) && !this.isAdmin) {
        return
      }
      const votingQuestion = session.listOfSessionVotings[0]
      this.$router.push({ name: 'SessionVoting', query: {...this.$route.query, session_voting_id: votingQuestion.id, session_id: session.id } })
    },
    formattedTimeString (date) {
      return dayjs(date).tz('Asia/Seoul').format('HH:mm')
    },
    isPastSession (session) {
      const now = dayjs().tz('Asia/Seoul')
      const endTime = dayjs(session.scheduledEndTime).tz('Asia/Seoul')
      return now.isAfter(endTime)
    },
    isNotStartedSession (session) {
      const now = dayjs().tz('Asia/Seoul')
      const startTime = dayjs(session.scheduledStartTime).tz('Asia/Seoul')

      return now.isBefore(startTime) 
    },
    isLiveSession (session) {
      const now = dayjs().tz('Asia/Seoul')
      const startTime = dayjs(session.scheduledStartTime).tz('Asia/Seoul')
      const endTime = dayjs(session.scheduledEndTime).tz('Asia/Seoul').add(20, 'minute')
      return now.isAfter(startTime) && now.isBefore(endTime)
    },
    startingTime (session) {
      const now = dayjs().tz('Asia/Seoul')
      const startTime = dayjs(session.scheduledStartTime).tz('Asia/Seoul')
      
      if (startTime.isSame(now, 'day')) {
        const hoursUntilStart = startTime.diff(now, 'hour')
        const minutesUntilStart = startTime.diff(now, 'minute')
        return (hoursUntilStart >= 1) ? `Starts in ${hoursUntilStart} hours` : `Starts in ${minutesUntilStart} min.`
      } else if (startTime.isSame(now.add(1, 'day'), 'day')) {
        return 'Starts Tomorrow'
      } else {
        return `Starts on ${startTime.format('MMMM D')}`
      }
    },
  },
  mounted () {
    this.getSessionVotingQuestions()
  },
}
</script>
